import { axios } from '@pukka/auth';
import { MOKA_SERVICE_HOST } from '@pukka/common/constants';
import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { format } from 'date-fns';
import { print } from 'graphql/language/printer';

import { ReportingModel } from '../../models';

import { SalesSummaryQuery, TopItemsQuery, TransactionSummaryReportQuery } from './queries.gql';

const REPORTING_URL = `${MOKA_SERVICE_HOST}/reporting-revamp/graphql`;

const parseDate = (date: Date, isStart: boolean, isAllDay?: boolean) => {
  const isNaN = Number.isNaN(date.getTime());
  const finalDate = isNaN ? new Date() : date;

  if (isAllDay || isNaN) {
    if (isStart) {
      finalDate.setHours(0, 0, 0);
    } else {
      finalDate.setHours(23, 59, 59);
    }
  }

  return format(finalDate, 'yyyy-MM-dd HH:mm:ss');
};

async function fetchReportingSummaryAndTopItems(outletIds: number[], startDate: Date, endDate: Date) {
  const variables = {
    outletIds,
    startDate: parseDate(startDate, true, true),
    endDate: parseDate(endDate, false, true),
  };

  const [summary, topItems] = await Promise.all([
    axios.post<ReportingModel.SalesSummaryResponse>(REPORTING_URL, {
      query: print(SalesSummaryQuery),
      variables,
    }),
    axios.post<ReportingModel.TopItemsResponse>(REPORTING_URL, {
      query: print(TopItemsQuery),
      variables,
    }),
  ]);

  return { ...summary.data.data, ...topItems.data.data };
}

// Success Transaction Summary
// eslint-disable-next-line @typescript-eslint/no-unused-vars
async function fetchTransactionSummaryReport(outletIds: number[], startDate: Date, endDate: Date, receipt: string) {
  const variables = {
    outletIds,
    startDate: parseDate(startDate, true),
    endDate: parseDate(endDate, false),
    // TODO: enable this
    // receiptNumber: receipt,
  };

  const result = await axios.post<ReportingModel.TransactionSummaryReportResponse>(REPORTING_URL, {
    query: print(TransactionSummaryReportQuery),
    variables,
  });

  return { ...result.data.data };
}

function useReportingSummaryAndTopItems(
  outletIds: number[],
  startDate: Date,
  endDate: Date,
  options: UseQueryOptions<Awaited<ReturnType<typeof fetchReportingSummaryAndTopItems>>> = {},
) {
  return useQuery<Awaited<ReturnType<typeof fetchReportingSummaryAndTopItems>>>(
    ['reporting-summary'],
    fetchReportingSummaryAndTopItems.bind({}, outletIds, startDate, endDate),
    {
      refetchOnMount: false,
      ...options,
    },
  );
}

function useTransactionSummaryReport(
  outletIds: number[],
  startDate: Date,
  endDate: Date,
  receipt: string,
  options: UseQueryOptions<Awaited<ReturnType<typeof fetchTransactionSummaryReport>>>,
) {
  return useQuery<Awaited<ReturnType<typeof fetchTransactionSummaryReport>>>(
    ['transaction-summary', outletIds, startDate, endDate, receipt],
    fetchTransactionSummaryReport.bind({}, outletIds, startDate, endDate, receipt),
    {
      refetchOnMount: false,
      ...options,
    },
  );
}

export {
  REPORTING_URL,
  useReportingSummaryAndTopItems,
  fetchReportingSummaryAndTopItems,
  useTransactionSummaryReport,
  fetchTransactionSummaryReport,
};
